import type { BundleIndex } from "../lib/types";
import { SearchBar } from "./SearchBar";

type Props = {
  index: BundleIndex;
  onReset: () => void;
  query: string;
  onQueryChange: (v: string) => void;
  matchedRunCount: number;
  totalMatchCount: number;
  hideEmpty: boolean;
  onToggleHideEmpty: () => void;
  emptyRunCount: number;
  actionFilter: string;
  onSelectAction: (action: string) => void;
  filteredRunCount: number;
};

// Header strip — date, total runs/packages, day-level counts (clickable
// filter chips), search, hide-empty filter, button to load a different
// bundle.
//
// Click a chip → filter the run list to runs containing at least one
// decision with that action. Click an active chip again to clear.
// The active state lives in URL ?action=<key> so the filtered view is
// shareable.
export function Summary({
  index,
  onReset,
  query,
  onQueryChange,
  matchedRunCount,
  totalMatchCount,
  hideEmpty,
  onToggleHideEmpty,
  emptyRunCount,
  actionFilter,
  onSelectAction,
  filteredRunCount,
}: Props) {
  const c = index.summary.counts;
  return (
    <header className="summary">
      <div className="summary__top">
        <div className="summary__left">
          <h1>kilochain · {index.date}</h1>
          <p className="summary__meta">
            {index.summary.totalRuns} runs · {index.summary.totalPackages} packages ·
            bundle generated {new Date(index.generatedAt).toLocaleString()}
          </p>
        </div>
        <div className="summary__counts" role="group" aria-label="Filter runs by action">
          <Chip label="block"    count={c.block}    cls="chip--block"    active={actionFilter === "block"}    onClick={() => onSelectAction("block")} />
          <Chip label="review"   count={c.review}   cls="chip--review"   active={actionFilter === "review"}   onClick={() => onSelectAction("review")} />
          <Chip label="safe"     count={c.none}     cls="chip--none"     active={actionFilter === "none"}     onClick={() => onSelectAction("none")} />
          <Chip label="skip"     count={c.skip}     cls="chip--skip"     active={actionFilter === "skip"}     onClick={() => onSelectAction("skip")} />
          <Chip label="error"    count={c.error}    cls="chip--error"    active={actionFilter === "error"}    onClick={() => onSelectAction("error")} />
          <Chip label="filtered" count={c.filtered} cls="chip--filtered" active={actionFilter === "filtered"} onClick={() => onSelectAction("filtered")} />
          <button
            type="button"
            className="chip chip--clickable chip--clear"
            onClick={() => { if (actionFilter) onSelectAction(actionFilter); }}
            disabled={!actionFilter}
            title={actionFilter ? "Clear filter (show all runs)" : "No filter active"}
          >
            <span className="chip__label">{actionFilter ? "✕ clear" : "all"}</span>
          </button>
        </div>
        <button className="summary__reset" onClick={onReset} title="Load a different bundle">
          Load different bundle
        </button>
      </div>
      {actionFilter && (
        <div className="summary__filter-banner" role="status">
          <span className="summary__filter-banner-icon">▾</span>
          <span>
            Showing only runs with at least one <strong>{labelFor(actionFilter)}</strong> decision.
            {" "}
            <span className="summary__filter-banner-count">
              {filteredRunCount} of {index.summary.totalRuns} runs match.
            </span>
          </span>
          <button
            className="summary__filter-clear"
            onClick={() => onSelectAction(actionFilter)}
            title="Clear filter (show all runs)"
          >
            ✕ clear
          </button>
        </div>
      )}
      <div className="summary__searchrow">
        <SearchBar
          value={query}
          onChange={onQueryChange}
          matchedRunCount={matchedRunCount}
          totalMatchCount={totalMatchCount}
          totalRuns={index.summary.totalRuns}
        />
        {emptyRunCount > 0 && (
          <button
            className={`summary__empty-toggle ${hideEmpty ? "summary__empty-toggle--active" : ""}`}
            onClick={onToggleHideEmpty}
            title={
              hideEmpty
                ? `Currently hiding ${emptyRunCount} empty run(s). Click to show.`
                : `Hide ${emptyRunCount} empty run(s) from the left rail.`
            }
          >
            {hideEmpty ? `✓ hide empty (${emptyRunCount})` : `hide empty (${emptyRunCount})`}
          </button>
        )}
      </div>
    </header>
  );
}

function labelFor(action: string): string {
  // chip labels don't 1:1 match decision keys (we display "safe" for
  // the "none" verdict). Reverse the mapping here so the banner reads
  // naturally — "showing only runs with at least one 'safe' decision".
  if (action === "none") return "safe";
  return action;
}

function Chip({
  label,
  count,
  cls,
  active,
  onClick,
}: {
  label: string;
  count: number;
  cls: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`chip chip--clickable ${cls} ${count === 0 ? "chip--empty" : ""} ${active ? "chip--active" : ""}`}
      onClick={onClick}
      aria-pressed={active}
      title={active ? `Clear filter (show all runs)` : `Filter to runs with at least one '${label}' decision`}
    >
      <span className="chip__count">{count}</span>
      <span className="chip__label">{label}</span>
    </button>
  );
}
